import React from 'react';
import { Mail, Key, Eye, EyeOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';

export const EmailTab = ({
  settings, handleChange, showApiKey, setShowApiKey,
  sendingDigest, onSendDigestNow,
  testEmail, setTestEmail, sendingTest, onSendTestEmail,
}) => (
  <Card className="bg-white border-slate-200">
    <CardHeader>
      <CardTitle className="text-slate-900 flex items-center gap-2">
        <Mail className="w-5 h-5 text-indigo-700" />
        Configuração de Email (SendGrid)
      </CardTitle>
      <CardDescription className="text-slate-500">
        Chave API global do SendGrid para envio de emails da plataforma e dos canais
      </CardDescription>
    </CardHeader>
    <CardContent className="space-y-6">
      {/* API Key */}
      <div className="space-y-2">
        <Label className="text-slate-600 flex items-center gap-2">
          <Key className="w-4 h-4" />
          SendGrid API Key
        </Label>
        <div className="relative">
          <Input
            type={showApiKey ? "text" : "password"}
            value={settings.sendgrid_api_key}
            onChange={(e) => handleChange('sendgrid_api_key', e.target.value)}
            placeholder={settings.sendgrid_api_key_masked || 'SG.xxxxxxxxxxxxx'}
            className="bg-white border-slate-300 text-slate-900 pr-10"
            data-testid="sendgrid-api-key-input"
          />
          <button
            type="button"
            onClick={() => setShowApiKey(!showApiKey)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
          >
            {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-xs text-slate-500">
          {settings.sendgrid_api_key_masked
            ? `Configurado: ${settings.sendgrid_api_key_masked}. Deixe em branco para manter.`
            : 'Obtenha a sua chave em '}
          {!settings.sendgrid_api_key_masked && (
            <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="text-indigo-700 hover:underline">
              app.sendgrid.com
            </a>
          )}
        </p>
      </div>

      <Separator className="bg-slate-700" />

      {/* Sender Config */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-600">Email Remetente</Label>
          <Input
            type="email"
            value={settings.sender_email}
            onChange={(e) => handleChange('sender_email', e.target.value)}
            placeholder="noreply@cognietica.pt"
            className="bg-white border-slate-300 text-slate-900"
            data-testid="sender-email-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-600">Nome Remetente</Label>
          <Input
            value={settings.sender_name}
            onChange={(e) => handleChange('sender_name', e.target.value)}
            placeholder="CogniEtica"
            className="bg-white border-slate-300 text-slate-900"
            data-testid="sender-name-input"
          />
        </div>
      </div>

      <Separator className="bg-slate-700" />

      {/* Allow Custom Email */}
      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
        <div>
          <Label className="text-slate-900">Permitir Email Personalizado por Cliente</Label>
          <p className="text-sm text-slate-500 mt-1">
            Clientes podem usar a sua própria chave SendGrid
          </p>
        </div>
        <Switch
          checked={settings.allow_tenant_custom_email}
          onCheckedChange={(checked) => handleChange('allow_tenant_custom_email', checked)}
          data-testid="allow-custom-email-toggle"
        />
      </div>

      {/* Alert on delivery failures */}
      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
        <div>
          <Label className="text-slate-900">Alertar Super Admin em falhas de entrega</Label>
          <p className="text-sm text-slate-500 mt-1">
            Enviar email aos Super Admins quando um canal tem emails devolvidos, spam ou falha de envio (máx. 1 por canal a cada 3h)
          </p>
        </div>
        <Switch
          checked={settings.alert_delivery_failures}
          onCheckedChange={(checked) => handleChange('alert_delivery_failures', checked)}
          data-testid="alert-delivery-failures-toggle"
        />
      </div>

      {/* Daily delivery digest */}
      <div className="p-4 bg-slate-50 rounded-lg space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-slate-900">Resumo diário de entrega</Label>
            <p className="text-sm text-slate-500 mt-1">
              Um único email por dia com todos os canais com problemas nas últimas 24h (evita encher a caixa em picos de falhas)
            </p>
          </div>
          <Switch
            checked={settings.delivery_digest_enabled}
            onCheckedChange={(checked) => handleChange('delivery_digest_enabled', checked)}
            data-testid="delivery-digest-toggle"
          />
        </div>
        {settings.delivery_digest_enabled && (
          <div className="flex items-end gap-4 flex-wrap">
            <div className="w-40">
              <Label className="text-slate-600">Hora de envio (UTC)</Label>
              <Input
                type="number" min={0} max={23}
                value={settings.delivery_digest_hour ?? ''}
                onChange={(e) => handleChange('delivery_digest_hour', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                className="bg-white border-slate-300 text-slate-900"
                data-testid="delivery-digest-hour-input"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              onClick={onSendDigestNow}
              disabled={sendingDigest}
              data-testid="send-digest-now-btn"
            >
              {sendingDigest ? 'A enviar...' : 'Enviar resumo agora'}
            </Button>
          </div>
        )}
      </div>

      <Separator className="bg-slate-200" />

      {/* Send a test email */}
      <div className="space-y-2">
        <Label className="text-slate-900 font-semibold">Enviar email de teste</Label>
        <p className="text-sm text-slate-500">
          Valide a entrega a qualquer momento, sem criar uma denúncia. O estado aparece no Registo de Emails.
        </p>
        <div className="flex gap-2">
          <Input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="destinatario@exemplo.com"
            className="bg-white border-slate-300 text-slate-900"
            data-testid="test-email-input"
          />
          <Button
            type="button"
            className="bg-indigo-700 text-white hover:bg-indigo-800 shrink-0"
            onClick={onSendTestEmail}
            disabled={sendingTest}
            data-testid="send-test-email-btn"
          >
            {sendingTest ? 'A enviar...' : 'Enviar teste'}
          </Button>
        </div>
      </div>

      <Separator className="bg-slate-200" />

      {/* Event Webhook (delivery status) */}
      <div className="space-y-3">
        <Label className="text-slate-900 font-semibold">Webhook de Eventos (estado de entrega)</Label>
        <p className="text-sm text-slate-500">
          No SendGrid: <span className="font-medium">Settings → Mail Settings → Event Webhooks</span>. Cole este URL no campo "Post URL", ative "Signed Event Webhook" e copie a chave pública para o campo abaixo.
        </p>
        <div className="flex gap-2">
          <Input
            readOnly
            value={settings.event_webhook_url || ''}
            className="bg-slate-50 border-slate-300 text-slate-700 font-mono text-sm"
            data-testid="event-webhook-url"
          />
          <Button
            type="button"
            variant="outline"
            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 shrink-0"
            onClick={() => { navigator.clipboard?.writeText(settings.event_webhook_url || ''); toast.success('URL copiado'); }}
            data-testid="copy-webhook-url"
          >
            Copiar
          </Button>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-600">Chave Pública de Verificação do Webhook</Label>
          <Input
            value={settings.sendgrid_webhook_public_key || ''}
            onChange={(e) => handleChange('sendgrid_webhook_public_key', e.target.value)}
            placeholder="Cole aqui a Verification Key do SendGrid"
            className="bg-white border-slate-300 text-slate-900 font-mono text-sm"
            data-testid="webhook-public-key-input"
          />
          <p className="text-xs text-slate-500">
            Enquanto estiver vazia, os eventos são aceites sem verificação de assinatura. Recomendado preencher para produção.
          </p>
        </div>
      </div>
    </CardContent>
  </Card>
);
